import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl, type IntlShape } from "react-intl";
import {
  CreateCertificateDocument,
  DeleteCertificateDocument,
  MyselfExpertiseDataDocument,
  UpdateCertificateDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { audienceRelations, type Audience } from "@/lib/audience";
import { toDateInstant } from "@/lib/date-instant";

export interface CertificateFormInput {
  name: string;
  date: string;
  sharedWith?: ReadonlyArray<Audience | string>;
}

export type CertificateItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["certifications"]
>[number];

export interface UseCertificatesResult {
  items: readonly CertificateItem[];
  add: (input: CertificateFormInput) => Promise<void>;
  update: (id: string, input: CertificateFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: CertificateFormInput, intl: IntlShape) {
  if (!input.name.trim()) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.certificateNameRequired", defaultMessage: "Name is required." }),
    );
  }
  return {
    name: input.name,
    date: toDateInstant(input.date),
    ...(input.sharedWith !== undefined
      ? { sharedWith: audienceRelations(input.sharedWith) }
      : {}),
  };
}

export function useCertificates(): UseCertificatesResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const [createMut, createState] = useMutation(CreateCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: CertificateFormInput) {
    if (!personId) throw new Error("useCertificates: no personId yet");
    await createMut({
      variables: {
        input: { data: { ...toDataInput(input, intl), person: { id: personId } } },
      },
    });
  }

  async function update(id: string, input: CertificateFormInput) {
    await updateMut({ variables: { id, input: { data: toDataInput(input, intl) } } });
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.certifications ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
